"use client"

import { ChartDataPoint } from "./types"

/**
 * Smooths an array of data points by applying a moving average
 * @param data Original data array
 * @param windowSize Size of the moving average window
 * @returns Smoothed data array
 */
export const smoothData = (data: ChartDataPoint[], windowSize: number) => {
  if (data.length <= windowSize) {
    return data // Not enough data to smooth
  }

  const result = []
  const halfWindow = Math.floor(windowSize / 2)

  // Add first points that can't have a complete window
  for (let i = 0; i < halfWindow; i++) {
    result.push(data[i])
  }

  // Apply moving average to middle points
  for (let i = halfWindow; i < data.length - halfWindow; i++) {
    let sum = 0
    for (let j = i - halfWindow; j <= i + halfWindow; j++) {
      sum += data[j].y
    }
    result.push({
      x: data[i].x,
      y: sum / windowSize,
    })
  }

  // Add last points that can't have a complete window
  for (let i = data.length - halfWindow; i < data.length; i++) {
    result.push(data[i])
  }

  return result
}

/**
 * Reduces the number of data points by sampling
 * @param data Original data array
 * @param targetPoints Approximate number of points to keep
 * @returns Reduced data array
 */
export const reduceDataPoints = (
  data: ChartDataPoint[],
  targetPoints: number,
) => {
  if (data.length <= targetPoints) {
    return data // Already has fewer points than target
  }

  const interval = Math.ceil(data.length / targetPoints)
  const result = []

  for (let i = 0; i < data.length; i += interval) {
    result.push(data[i])
  }

  // Always include the last point to ensure the end value is accurate
  if (result[result.length - 1] !== data[data.length - 1]) {
    result.push(data[data.length - 1])
  }

  return result
}
